import React, { useEffect, useRef, useState } from "react";
// import React from 'react';
import "../CSS/Dashboard.css";
import "../CSS/CreatePage.css";
// import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { Container, Row, Form, Col, Button, Spinner, Alert } from "react-bootstrap";
import ProgressOverlay from "../components/UI/ProgressOverlay";
import useFakeProgress from "../Utils/useFakeProgress";
import EnterEmailDialog from "../components/UI/EnterEmailDialog";
import LinkCreatedDialog from "../components/UI/LinkCreatedDialog";
import { StorageUtils } from "../Utils/StorageUtils";
import { LinkService } from "../Services/LinkService";
import { DESCRIPTION, ALT_NAME } from "../config/branding.js";
import { UserStatusProvider, useUserStatus } from "../contexts/UserStatusContext";
import UpgradeModal from "../components/UI/UpgradeModal";
import UpgradeCelebrationModal from "../components/UI/UpgradeCelebrationModal";
import { DebugProvider } from "../contexts/DebugContext";
import DebugToggle from "../components/Debug/DebugToggle";
import RemainingLinksDebugPanel from "../components/Debug/RemainingLinksDebugPanel";

const CreatePageContent = () => {
  const { 
    canCreateLinks, 
    decrementRemainingLinks, 
    showUpgradeCelebration, 
    closeUpgradeCelebration,
    userStatus,
    remainingLinks,
    getPlanDisplayName,
    isPro,
    isStarter,
    isLoadingGenerations,
    syncFreeGenerationStatus
  } = useUserStatus();
  
  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isBaseContainerVisible, setIsBaseContainerVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [showProgress, setShowProgress] = useState(false);
  const progress = useFakeProgress({ totalMs: 45000, cap: 92 });

  const [errors, setErrors] = useState({});
  const createdLinkRef = useRef(null);
  const linkStatusRef = useRef(null);
  const processingErrorRef = useRef(null);
  const [formData, setFormData] = useState({
    link: "",
  });

  const statusPollingRef = useRef(null);

  useEffect(() => {
    return () => {
      if (statusPollingRef.current) {
        clearInterval(statusPollingRef.current);
        statusPollingRef.current = null;
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Validate form
  const validateForm = () => {
  const newErrors = {};

  // Check if the link is empty
  if (!formData.link.trim()) {
    newErrors.link = "Link is required";
  } else {
    // Validate the URL format
    const urlPattern = /^(https?:\/\/)?([\da-z.-]+\.[a-z.]{2,6})(\/[^\s]*)?$/i;
    if (!urlPattern.test(formData.link.trim())) {
      newErrors.link = "Please enter a valid URL";
    }
  }

  return newErrors;
};

  // Check if user is signed in
  const isUserSignedIn = () => {
    const userData = StorageUtils.getUserData();
    return userData && userData.user && userData.user.id && userData.token;
  };

  const extractLinkFromResponse = (response) => {
    const data = response?.data;
    if (!data) return null;
    if (data.link) return data.link;
    if (data.data) return data.data;
    return data;
  };

  const extractLinkId = (linkData, response) => {
    return linkData?.id || linkData?.linkId || response?.data?.linkId || response?.data?.id;
  };

  const updateStatusState = (statusData) => {
    linkStatusRef.current = statusData?.status || null;
    processingErrorRef.current = statusData?.processingError || null;
    createdLinkRef.current = {
      ...(createdLinkRef.current || {}),
      ...statusData
    };
  };

  const stopStatusPolling = () => {
    if (statusPollingRef.current) {
      clearInterval(statusPollingRef.current);
      statusPollingRef.current = null;
    }
  };

  const pollLinkStatusUntilDone = (linkId) => {
    if (!linkId) return;
    stopStatusPolling();

    const pollOnce = async () => {
      try {
        const statusResponse = await LinkService.fetchLinkStatus(linkId);
        const statusData = statusResponse?.data || {};
        updateStatusState(statusData);

        if (statusData.status === 'completed') {
          stopStatusPolling();
          setApiResponse({
            success: statusData.message || 'Link ready! 🎉 Navigate to the Dashboard to view your link.'
          });
        } else if (statusData.status === 'failed') {
          stopStatusPolling();
          setApiResponse({
            error: statusData.processingError || statusData.message || 'Link processing failed. Please try again.'
          });
        }
      } catch (error) {
        console.error('❌ Error polling link status:', error);
      }
    };

    pollOnce();
    statusPollingRef.current = setInterval(pollOnce, 4000);
  };

  // Create link directly for signed-in users
  const createLinkDirectly = async () => {
    try {
      // Show fake progress when starting the network request
      setShowProgress(true);
      progress.start();
      setLoading(true);
      
      // For authenticated users, send originalUrl and user's email
      const userData = StorageUtils.getUserData();
      const userEmail = userData?.user?.email;
      
      const payload = {
        originalUrl: formData.link,
        email: userEmail, // Send user's email instead of caption
      };
      
      // Send payload to create link
      console.log('Creating link with payload:', payload);
      
      const response = await LinkService.createLink(payload);
      const linkData = extractLinkFromResponse(response);
      const linkId = extractLinkId(linkData, response);

      createdLinkRef.current = linkData;
      updateStatusState(linkData || response?.data || {});
      
      // Sync generation status from backend (backend now handles decrement)
      await syncFreeGenerationStatus();
      
      const isProcessing = response?.status === 202 || linkData?.status === 'processing';
      setApiResponse({
        success: response?.data?.message || (
          isProcessing
            ? 'Link accepted and processing. We will update once it is ready.'
            : 'Link created successfully! 🎉 Navigate to the Dashboard to view your link.'
        )
      });
      // Finish and hide progress shortly after success
      progress.complete();
      setTimeout(() => setShowProgress(false), 600);
      console.log('✅ Link created successfully:', response.data);

      if (isProcessing) {
        pollLinkStatusUntilDone(linkId);
      }
      
      // Reset form but keep success message visible until user dismisses it
      setFormData({ link: '' });
      
    } catch (error) {
      console.error('❌ Error creating link:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        fullError: error
      });
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          `Server error (${error.response?.status || 'Unknown'}): ${error.message}`;
      
      setApiResponse({ error: errorMessage });
      setTimeout(() => {
        setApiResponse(null);
      }, 5000); // Show error longer
    } finally {
      // Ensure progress overlay is hidden if request ends with error
      if (!apiResponse?.success) {
        setTimeout(() => setShowProgress(false), 300);
        progress.reset();
      }
      setLoading(false);
    }
  };

  const handleContinueClick = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    
    if (Object.keys(validationErrors).length === 0) {
      console.log("Continue button clicked");
      
      // Check if user can create links based on their plan
      if (!canCreateLinks()) {
        setShowUpgradeModal(true);
        return;
      }
      
      // Check if user is signed in
      if (isUserSignedIn()) {
        // Signed-in users: skip email dialog and create link directly
        await createLinkDirectly();
      } else {
        // Non-signed-in users: show email collection dialog
        setIsVisible(true);
      }
      
      console.log("Form submitted successfully!", formData);
    } else {
      setErrors(validationErrors);
    }
  };

  const handleContinueEmailClick = () => {
    console.log("Continue email button clicked");
    
    // Double-check if user can still create links before proceeding
    if (!canCreateLinks()) {
      console.log("User ran out of links during email flow, showing upgrade modal");
      setIsVisible(false); // Hide email dialog
      setShowUpgradeModal(true); // Show upgrade modal
      return;
    }
    
    // Decrement remaining links for starter users when creating via email flow
    decrementRemainingLinks();
    
    setIsBaseContainerVisible(true); // Show the confirmation box
  };
  return (
    
    <Container fluid>
      {/* Global progress overlay while creating link for signed-in flow */}
      <ProgressOverlay visible={showProgress} percent={progress.percent} />

      {isVisible ? (
        <div style={{ marginTop: "100px" }}>
          <EnterEmailDialog 
            link={formData.link} 
            onContinue={handleContinueEmailClick}
            canCreateLinks={canCreateLinks()}
            onUpgradeNeeded={() => {
              setIsVisible(false);
              setShowUpgradeModal(true);
            }}
          />
          {isBaseContainerVisible ? <LinkCreatedDialog /> : null}
        </div>
) : (
        <>
        <Row
          className="justify-content-center mt-10"
          style={{ marginTop: "100px" }}
        >
          <Col xs={12} className="text-center">
            <h1>
              {DESCRIPTION}
            </h1>
            <p style={{ color: "#6c757d" }}>
              Track and analyze your ChatGPT & Gemini conversations. <br />
              Join thousands using {ALT_NAME} to understand their content's reach.
            </p>
          </Col>
          <Col xs={12} md={6} lg={4}>
            <div className="container-box">
              <Form>
                <Form.Group className="mb-3" controlId="chatGPTLink">
                  <Form.Control
                    type="text"
                    placeholder="Enter your ChatGPT or Gemini link here"
                    className="custom-text-field"
                    name="link"
                    value={formData.link} onChange={handleInputChange} isInvalid={!!errors.link}
                  />
                  <Form.Control.Feedback type="invalid">{errors.link}</Form.Control.Feedback>
                </Form.Group>
                <center>
                  <Button
                    variant={!canCreateLinks() ? "outline-secondary" : "dark"}
                    className="custom-button"
                    onClick={handleContinueClick}
                    disabled={loading || isLoadingGenerations}
                  >
                    {loading ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                        {' Please Wait...'}
                      </>
                    ) : isLoadingGenerations ? (
                      '⏳ Loading...'
                    ) : !canCreateLinks() ? (
                      '🚫 Upgrade to Continue'
                    ) : (
                      'Continue'
                    )}
                  </Button>{" "}
                </center>
                
                {/* Show limit reached message for starter users without links */}
                {!isLoadingGenerations && userStatus === 'starter' && remainingLinks === 0 && (
                  <Alert variant="warning" className="mt-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>🚫 Weekly Limit Reached</strong>
                        <div className="small">You've used all {getPlanDisplayName()} links this week.</div>
                      </div>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setShowUpgradeModal(true)}
                      >
                        Upgrade
                      </Button>
                    </div>
                  </Alert>
                )}
                
                {apiResponse && (
                  <Alert 
                    variant={apiResponse.error ? 'danger' : 'success'} 
                    className="mt-3"
                    dismissible
                    onClose={() => setApiResponse(null)}
                  >
                    {apiResponse.error || apiResponse.success}
                  </Alert>
                )}
              </Form>
            </div>
          </Col>
        </Row>

        {/* How it works steps */}

        <Row className="justify-content-center mt-4 mb-5">
          {/* How to steps column */}
          <Col xs={12} md={8} lg={7}>
            <section className="howto-steps" aria-label="How to upload trackable links">
              <h3 className="howto-title">How to upload a trackable link</h3>
              <div className="howto-grid">
                <div className="step-card">
                  <div className="number-badge">1</div>
                  <div className="step-body">
                    <div className="step-title">Copy your conversation link</div>
                    <div className="step-text text-muted">
                      In ChatGPT or Google Gemini, use Share to generate a public link, then copy it.
                    </div>
                  </div>
                </div>
                <div className="step-card">
                  <div className="number-badge">2</div>
                  <div className="step-body">
                    <div className="step-title">Paste it above and continue</div>
                    <div className="step-text text-muted">
                      Paste the URL into the box and press Continue. We'll create a short, trackable link.
                    </div>
                  </div>
                </div>
                <div className="step-card">
                  <div className="number-badge">3</div>
                  <div className="step-body">
                    <div className="step-title">Share and track</div>
                    <div className="step-text text-muted">
                      Share your new ThreadShare link anywhere. See visits, redirects, and engagement in your Dashboard.
                    </div>
                  </div>
                </div>
              </div>
              <div className="howto-footnote">
                Supported: ChatGPT shared links and Google Gemini public links.
              </div>
              <div style={{ 
                marginTop: '1rem', 
                padding: '0.75rem 1rem', 
                backgroundColor: '#f0f9ff', 
                border: '1px solid #bae6fd', 
                borderRadius: '8px',
                fontSize: '0.875rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <i className="fas fa-puzzle-piece" style={{ color: '#0284c7' }}></i>
                  <span style={{ color: '#0c4a6e' }}>
                    <strong>Recommended:</strong> For a more reliable and faster experience,{' '}
                    <a 
                      href="https://chromewebstore.google.com/detail/mkdemnaiienmhffacabkpboljfcdmfel?utm_source=item-share-cb" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: '#0284c7', textDecoration: 'underline', fontWeight: '500' }}
                    >
                      download our browser extension
                    </a>
                    . Save conversations from ChatGPT, Claude, or Gemini with one click—no copying or pasting needed.
                  </span>
                </div>
              </div>
            </section>
          </Col>
        </Row>
        </>
      )}
      
      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        trigger="create"
      />

      {/* Upgrade Celebration Modal */}
      <UpgradeCelebrationModal 
        isOpen={showUpgradeCelebration}
        onClose={closeUpgradeCelebration}
        planName={userStatus === 'pro' ? 'Pro' : 'Starter'}
      />

      {/* Debug Components */}
      <DebugToggle position="bottom-left" />
      <RemainingLinksDebugPanel />
    </Container>
  );
};

// Remove the wrapper since providers are now at root level
export default CreatePageContent;
